import { memo, MouseEvent, ReactNode, useMemo } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { UserSearch } from 'lucide-react';
import { isNullish } from 'remeda';

import Chip, { ChipProps } from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { GameInfoTooltip } from '../gameInfoTooltip/GameInfoTooltip';
import { formatAmount, formatNumber, isSpinResultAmount } from '../utils';

import css from './ProviderTransactionsTable.module.css';

import { DataTable } from 'v2/components/dataTable/DataTable';
import { LONG_DASH } from 'v2/constants';
import { TableColumn } from 'v2/types';
import { ProviderTransaction, ProviderTransactionActionType } from 'v2/types/ProviderTransactionModel';
import { copyText } from 'v2/utils';

interface ProviderTransactionsTableProps {
  data?: ProviderTransaction[];
  showUserColumn: boolean;
  onSelectUser: (userId: string) => void;
}

const ACTION_CHIP_PROPS: Record<string, Pick<ChipProps, 'color' | 'variant'>> = {
  [ProviderTransactionActionType.BET]: { color: 'default' },
  [ProviderTransactionActionType.WIN]: { color: 'success' },
  [ProviderTransactionActionType.ROLLBACK]: { color: 'warning' },
};

const OTHER_ACTION_CHIP_PROPS: Pick<ChipProps, 'color' | 'variant'> = { variant: 'outlined' };

const handleCopy = (text: string) => (e: MouseEvent) => {
  e.stopPropagation();
  copyText(text);
};

const renderAmount = (row: ProviderTransaction): ReactNode => {
  const amount = formatAmount(row);

  if (isNullish(amount)) {
    return LONG_DASH;
  }

  const content = <span className={clsx(row.canceled && css.canceledAmount)}>{amount}</span>;

  return isSpinResultAmount(row) ? (
    <Tooltip title="итог спина">
      <span className={css.spinResult}>{content}</span>
    </Tooltip>
  ) : (
    content
  );
};

const renderExpandedRow = (row: ProviderTransaction): ReactNode => {
  const fields: [string, string | null][] = [
    ['Action ID', row.actionId],
    ['Balance ID', row.balanceId],
    ['Tournament ID', row.tournamentId],
    ['User ID', row.userId],
    ['Record ID', row.id],
    ['Date (ISO)', row.date],
  ];

  return (
    <dl className={css.details}>
      {fields.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd className={css.mono}>
            {isNullish(value) ? (
              LONG_DASH
            ) : (
              <span className={css.copyable} onClick={handleCopy(value)}>
                {value}
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const getRowClassName = (row: ProviderTransaction) => (row.canceled ? css.canceledRow : undefined);

export const ProviderTransactionsTable = memo<ProviderTransactionsTableProps>(
  ({ data, showUserColumn, onSelectUser }) => {
    const columns: TableColumn<ProviderTransaction>[] = useMemo(
      () => [
        {
          key: 'date',
          label: 'Date',
          style: { width: 190, whiteSpace: 'nowrap' },
          render: (row) => dayjs(row.date).format('DD.MM.YYYY HH:mm:ss'),
        },
        ...(showUserColumn
          ? [
              {
                key: 'userId',
                label: 'User',
                style: { width: 200 },
                render: (row: ProviderTransaction) => (
                  <div className={css.userCell}>
                    <span className={clsx(css.mono, 'truncate')}>{row.userId}</span>

                    <Tooltip title="Показать спины юзера">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectUser(row.userId);
                        }}
                      >
                        <UserSearch width={16} height={16} />
                      </IconButton>
                    </Tooltip>
                  </div>
                ),
              },
            ]
          : []),
        {
          id: 'providerName',
          key: 'providerName',
          label: 'Provider',
          style: { width: 130 },
        },
        {
          key: 'gameId',
          label: 'Game',
          ellipsis: true,
          style: { width: 200 },
          render: (row) =>
            isNullish(row.gameId) ? LONG_DASH : <GameInfoTooltip providerName={row.providerName} gameId={row.gameId} />,
        },
        {
          key: 'actionType',
          label: 'Action',
          style: { width: 190 },
          render: (row) => (
            <div className={css.chips}>
              <Chip
                size="small"
                label={row.actionType}
                {...(ACTION_CHIP_PROPS[row.actionType] ?? OTHER_ACTION_CHIP_PROPS)}
              />

              {row.canceled && <Chip size="small" label="canceled" color="error" variant="outlined" />}
            </div>
          ),
        },
        {
          key: 'amount',
          label: 'Amount',
          align: 'right',
          style: { width: 120 },
          render: renderAmount,
        },
        {
          key: 'balanceAfter',
          label: 'Balance after',
          align: 'right',
          style: { width: 130 },
          render: (row) => (isNullish(row.balanceAfter) ? LONG_DASH : formatNumber(row.balanceAfter)),
        },
      ],
      [showUserColumn, onSelectUser]
    );

    return (
      <DataTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        getRowClassName={getRowClassName}
        renderExpandedRow={renderExpandedRow}
      />
    );
  }
);

ProviderTransactionsTable.displayName = 'ProviderTransactionsTable';
